import { StrictMode } from 'react';
import { createRoot, Root } from 'react-dom/client';
import Ebs from './Ebs'; // Ensure this path is correct and the Ebs component is properly exported
import { create, cssomSheet, strict } from 'twind';
import AuthContextProvider from './contexts/auth-context';
// disabled GoogleTagManager for now because of RGPD compliance
// import { GoogleTagManager } from './GoogleTagManager';

// Converts kebab-case attributes to camelCase
function normalizeAttribute(attribute: string) {
  return attribute.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
}

(function loadGoogleFonts() {
  const fontUrl = 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Montserrat:ital,wght@0,100..900;1,100..900&family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Outfit:wght@100..900&family=Oswald:wght@200..700&family=PT+Sans:ital,wght@0,400;0,700;1,400;1,700&family=Amiri:ital,wght@0,400;0,700;1,400;1,700&family=Raleway:ital,wght@0,100..900;1,100..900&family=Jost:ital,wght@0,100..900;1,100..900&display=swap';

  // Check if fonts are already loaded
  if (!document.querySelector(`link[href="${fontUrl}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = fontUrl;
    document.head.appendChild(link);
  }
})()

export type EbsProps = {
  experienceId: string;
  lang: string;
  gtin: string;
  moduleMode: string;
};

const sheet = cssomSheet({ target: new CSSStyleSheet() });
export const { tw } = create({
  sheet,
  mode: strict,
  darkMode: 'class',
  theme: {},
});

class OrionExperience extends HTMLElement {
  #props?: EbsProps;
  #root?: Root;
  static get observedAttributes() {
    return ['experience-id', 'lang', 'gtin', 'module-mode'];
  }

  constructor() {
    super();
    const shadow = this.attachShadow({ mode: 'open' });

    const resetStyle = document.createElement('style');
    resetStyle.textContent = `
    :host {
      all: initial;
      display: block;
    }
    :host *, :host *::before, :host *::after {
      box-sizing: border-box;
    }
  `;

    shadow.appendChild(resetStyle);
    shadow.adoptedStyleSheets = [
      sheet.target,
    ];
  }
  connectedCallback() {
    this.updateProps();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue !== newValue) {
      this.updateProps();
    }
  }
  updateProps() {
    // Convert element attributes to props object
    this.#props = Array.from(this.attributes).reduce((props, attribute) => {
      const normalizedAttribute = normalizeAttribute(attribute.name);
      (props as any)[normalizedAttribute] = attribute.value;
      return props;
    }, {} as EbsProps);

    // Create a React root and render the Ebs component with the props
    if (this.#root) {
      this.#root.unmount();
    }
    this.#root = createRoot(this.shadowRoot!);
    this.#root.render(
      <StrictMode>
        <AuthContextProvider>
          {/* <GoogleTagManager /> */}
          <Ebs {...this.#props} />
        </AuthContextProvider>
      </StrictMode>
    );
  }

  disconnectedCallback() {
    this.#root?.unmount();
  }
}

customElements.define('orion-experience', OrionExperience);
